import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import { firstValueFrom, ReplaySubject, from, mergeMap, merge } from 'rxjs';
import { take, retry } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import Dexie from 'dexie';
import CryptoJS from 'crypto-js';
import enc from 'crypto-js/enc-utf8';

const NO_PATIENT_SEARCH = ['Organization'];
class FhirInterfaceService {
    constructor(http) {
        this.http = http;
    }
    init(config) {
        this.cacheConfig = config;
    }
    // Gets a bundle of resources from fhir and maps th em to an array of fhir resources
    async getResources(resourceType, count, page = 1, lastUpdated) {
        let result = [];
        const resourceURL = new URL(`${this.cacheConfig.fhirDetails.fhirUrl}/${this.cacheConfig.fhirDetails.tenancy}/${resourceType}?_sort=_lastUpdated${lastUpdated ? '&_lastUpdated=gt' + new Date(lastUpdated).toISOString() : ''}`);
        if (!NO_PATIENT_SEARCH.includes(resourceType)) {
            resourceURL.searchParams.set('patient', this.cacheConfig.fhirDetails.patientId);
        }
        if (count) {
            resourceURL.searchParams.set('_count', count.toString());
        }
        resourceURL.searchParams.set('_offset', page ? ((page - 1) * count).toString() : '0');
        const response = await firstValueFrom(this.http.get(resourceURL.toString()).pipe(take(1), retry(3)));
        result = response.entry?.map((entry) => entry.resource) || [];
        if (!count) {
            let next = response?.link?.find(l => l.relation === 'next');
            while (next) {
                const _response = await firstValueFrom(this.http.get(next.url).pipe(retry(3)));
                result = result.concat(_response?.entry?.map((entry) => entry.resource));
                next = _response?.link?.find(l => l.relation === 'next');
            }
        }
        return result;
    }
    async *getAllResources(resourceType, lastUpdated, device) {
        let result = [];
        const count = 500;
        const resourceUrlStrings = [];
        if (device && resourceType === 'Observation') {
            resourceUrlStrings.push(`${this.cacheConfig.fhirDetails.fhirUrl}/${this.cacheConfig.fhirDetails.tenancy}/${resourceType}?patient=${this.cacheConfig.fhirDetails.patientId}&_total=accurate&device:missing=true&_sort=_lastUpdated${lastUpdated ? '&_lastUpdated=gt' + new Date(lastUpdated).toISOString() : ''}`);
            resourceUrlStrings.push(`${this.cacheConfig.fhirDetails.fhirUrl}/${this.cacheConfig.fhirDetails.tenancy}/${resourceType}?patient=${this.cacheConfig.fhirDetails.patientId}&_total=accurate&device=${device}&_sort=_lastUpdated${lastUpdated ? '&_lastUpdated=gt' + new Date(lastUpdated).toISOString() : ''}`);
        }
        else if (NO_PATIENT_SEARCH.includes(resourceType)) {
            resourceUrlStrings.push(`${this.cacheConfig.fhirDetails.fhirUrl}/${this.cacheConfig.fhirDetails.tenancy}/${resourceType}?_total=accurate&_sort=_lastUpdated${lastUpdated ? '&_lastUpdated=gt' + new Date(lastUpdated).toISOString() : ''}`);
        }
        else {
            resourceUrlStrings.push(`${this.cacheConfig.fhirDetails.fhirUrl}/${this.cacheConfig.fhirDetails.tenancy}/${resourceType}?patient=${this.cacheConfig.fhirDetails.patientId}&_total=accurate&_sort=_lastUpdated${lastUpdated ? '&_lastUpdated=gt' + new Date(lastUpdated).toISOString() : ''}`);
        }
        for (let resourceUrlString of resourceUrlStrings) {
            let page = 1;
            const resourceURL = new URL(resourceUrlString);
            resourceURL.searchParams.set('_count', count.toString());
            try {
                const response = await firstValueFrom(this.http.get(resourceURL.toString()).pipe(take(1), retry(3)));
                result = response.entry?.map((entry) => entry.resource) || [];
                if (resourceType === 'Observation') {
                    result = result.map(res => {
                        return removeDataFromValueAttachment(res);
                    });
                }
                yield result;
                let chunkedRequestUrls = [];
                const nextLink = response.link?.find(l => l.relation === 'next')?.url;
                const _resourceURLString = nextLink?.split('?')?.shift() || resourceUrlString;
                while ((response?.total || count) > page * count) {
                    page++;
                    const _resourceURL = new URL(_resourceURLString);
                    if (response.id) {
                        _resourceURL.searchParams.set('_getpages', response.id);
                        _resourceURL.searchParams.set('_getpagesoffset', ((page - 1) * count).toString());
                    }
                    else if (page > 1) {
                        _resourceURL.searchParams.set('_offset', ((page - 1) * count).toString());
                    }
                    _resourceURL.searchParams.set('_count', count.toString());
                    chunkedRequestUrls.push(_resourceURL);
                    if (chunkedRequestUrls.length === 8 || (response.total || count) <= page * count) {
                        const responseBundles = await Promise.all(chunkedRequestUrls.map(_urlChunk => firstValueFrom(this.http.get(_urlChunk.toString()).pipe(retry(3)))));
                        for (const responseBundle of responseBundles) {
                            if (responseBundle !== null) {
                                let _result = responseBundle?.entry?.map((entry) => entry.resource) || [];
                                if (resourceType === 'Observation') {
                                    _result = _result.map(res => {
                                        return removeDataFromValueAttachment(res);
                                    });
                                }
                                yield _result;
                            }
                        }
                        chunkedRequestUrls = [];
                    }
                }
            }
            catch (e) {
                console.error(e);
            }
        }
        // return result;
    }
    // Get a single resource by it's URN
    async getResourceById(resourceType, id) {
        const resourceURL = new URL(`${this.cacheConfig.fhirDetails.fhirUrl}/${this.cacheConfig.fhirDetails.tenancy}/${resourceType}`);
        resourceURL.searchParams.set('identifier', id);
        const response = await firstValueFrom(this.http.get(resourceURL.toString()).pipe(retry(3)));
        return response?.entry?.[0].resource;
    }
    async putResource(resource) {
        if (this.cacheConfig.resources[resource.resourceType || 'Resource']?.isLocalOnly) {
            return null;
        }
        const resourceURL = new URL(`${this.cacheConfig.fhirDetails.fhirUrl}/${this.cacheConfig.fhirDetails.tenancy}/${resource.resourceType}/${resource.id}`);
        if (resource.resourceType && !NO_PATIENT_SEARCH.includes(resource.resourceType)) {
            resourceURL.searchParams.set('patient', this.cacheConfig.fhirDetails.patientId);
        }
        const postData = resource;
        try {
            const response = await firstValueFrom(this.http.put(resourceURL.toString(), postData));
            return response;
        }
        catch (error) {
            console.error(error);
            return null;
        }
    }
    async putResources(resources) {
        const resourceURL = new URL(`${this.cacheConfig.fhirDetails.fhirUrl}/${this.cacheConfig.fhirDetails.tenancy}?patient=${this.cacheConfig.fhirDetails.patientId}`);
        const postData = this.resourcesArrayToBundle(resources);
        try {
            const response = await firstValueFrom(this.http.post(resourceURL.toString(), postData));
            return response;
        }
        catch (error) {
            console.error(error);
            return null;
        }
    }
    resourcesArrayToBundle(resources) {
        const rBundle = {
            resourceType: 'Bundle',
            type: 'transaction',
            entry: resources
                .filter((resource) => !this.cacheConfig.resources[resource.resourceType || 'Resource']?.isLocalOnly)
                .map((resource) => {
                let method = 'POST';
                if (resource.meta?.lastUpdated) {
                    method = 'PUT';
                }
                return this.resourceToBundleEntry(Object.assign({}, resource), method);
            })
        };
        return rBundle;
    }
    resourceToBundleEntry(resource, method) {
        const entry = {
            resource,
            request: {
                method,
                url: method === 'POST' ? resource.resourceType : `${resource.resourceType}/${resource.id}`,
            },
            fullUrl: `urn:uuid:${resource.id}`
        };
        return entry;
    }
}
FhirInterfaceService.ɵfac = function FhirInterfaceService_Factory(t) { return new (t || FhirInterfaceService)(i0.ɵɵinject(i1.HttpClient)); };
FhirInterfaceService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: FhirInterfaceService, factory: FhirInterfaceService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FhirInterfaceService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: i1.HttpClient }]; }, null); })();

var EvictionPolicy;
(function (EvictionPolicy) {
    EvictionPolicy[EvictionPolicy["FirstInFirstOut"] = 0] = "FirstInFirstOut";
    EvictionPolicy[EvictionPolicy["LeastRecentlyUsed"] = 1] = "LeastRecentlyUsed";
})(EvictionPolicy || (EvictionPolicy = {}));
var ConflictPolicy;
(function (ConflictPolicy) {
    ConflictPolicy[ConflictPolicy["Ignore"] = 0] = "Ignore";
    ConflictPolicy[ConflictPolicy["Overwrite"] = 1] = "Overwrite";
    ConflictPolicy[ConflictPolicy["NewestByVersionId"] = 2] = "NewestByVersionId";
})(ConflictPolicy || (ConflictPolicy = {}));

const DB_VERSION = 4;
class CacheDB extends Dexie {
    constructor() {
        super('ngdexieliveQuery');
    }
    init() {
        this.version(DB_VERSION).stores({
            resources: 'resourceId, resourceType, [resourceId+resourceType], uuid, storedAt, syncedIdx, effectiveDateTime',
            syncState: 'resourceType, lastUpdated',
        });
    }
    setConfig(config) {
        this.cacheConfig = config;
    }
    wrapResourceForStorage(resource, synced) {
        const _resource = resource;
        const resourceJSON = JSON.stringify(_resource);
        const encryptedResource = CryptoJS.AES.encrypt(resourceJSON, this.cacheConfig.encryptionKey).toString();
        const storedAt = Date.now();
        let effectiveDateTime = storedAt;
        if (_resource.resourceType === 'Observation') {
            const observationDateTime = _resource.effectiveDateTime;
            if (observationDateTime) {
                effectiveDateTime = (new Date(observationDateTime)).getTime();
            }
        }
        else if (_resource.resourceType === 'Goal') {
            const goalDateTime = _resource.target?.dueDate;
            if (goalDateTime) {
                effectiveDateTime = (new Date(goalDateTime)).getTime();
            }
        }
        return {
            resource: encryptedResource,
            resourceId: _resource.id || '',
            resourceType: _resource.resourceType || 'Resource',
            uuid: (_resource.identifier?.length ? _resource.identifier[0].value : _resource.id) || '',
            storedAt,
            effectiveDateTime,
            synced,
            syncedIdx: synced ? 1 : 0,
            isLocalOnly: this.cacheConfig.resources[_resource.resourceType || 'Resource']?.isLocalOnly,
        };
    }
    unwrapResourceFromStorage(wrappedResource) {
        return {
            ...wrappedResource,
            resource: JSON.parse(CryptoJS.AES.decrypt(wrappedResource.resource, this.cacheConfig.encryptionKey).toString(enc)),
        };
    }
    unwrapResources(resources) {
        const result = [];
        resources.forEach((resource) => {
            if (resource) {
                result.push(this.unwrapResourceFromStorage(resource));
            }
        });
        return result;
    }
}
const db = new CacheDB();

class CacheInterfaceService {
    constructor() { }
    init(config) {
        db.init();
        this.setConfig(config);
    }
    setConfig(config) {
        this.cacheConfig = config;
        db.setConfig(config);
    }
    async clearAll() {
        await db.delete().then(() => db.open()).then(() => db.init()).catch(() => { });
    }
    async getResource(id, resourceType) {
        const wrappedResource = await db.resources.get({
            resourceId: id,
            resourceType,
        });
        return db.unwrapResourceFromStorage(wrappedResource)?.resource;
    }
    async getResources(resourceType, page) {
        let wrappedResources = await db.resources
            .where({ resourceType })
            .offset(page ? (page - 1) * this.cacheConfig.resources[resourceType].pageSize : 0)
            .limit(this.cacheConfig.resources[resourceType].pageSize).toArray();
        wrappedResources = db.unwrapResources(wrappedResources);
        const strippedResources = wrappedResources.map((wrappedResource) => {
            return {
                resource: wrappedResource.resource,
                synced: wrappedResource.synced,
            };
        });
        return strippedResources;
    }
    async getAllResourcesOfType(resourceType) {
        const PAGE_SIZE = this.cacheConfig.resources[resourceType].pageSize;
        let wrappedResources = await db.resources.orderBy('storedAt')
            .filter(resource => resource.resourceType === resourceType)
            .limit(PAGE_SIZE)
            .toArray();
        wrappedResources = db.unwrapResources(wrappedResources);
        const strippedResources = wrappedResources.map((wrappedResource) => {
            return {
                resource: wrappedResource.resource,
                synced: wrappedResource.synced,
            };
        });
        if (wrappedResources.length < PAGE_SIZE) {
            return strippedResources;
        }
        let result = [];
        result = result.concat(strippedResources);
        let partialResult = wrappedResources;
        let _wrappedResources = [];
        do {
            const lastEntry = partialResult[partialResult.length - 1];
            _wrappedResources = await db.resources.where('storedAt')
                .aboveOrEqual(lastEntry.storedAt)
                .filter(this.fastForward(lastEntry, 'resourceId', (resource) => resource.resourceType === resourceType))
                .limit(PAGE_SIZE)
                .toArray();
            partialResult = db.unwrapResources(_wrappedResources);
            const _strippedResources = partialResult.map((wrappedResource) => {
                return {
                    resource: wrappedResource.resource,
                    synced: wrappedResource.synced,
                };
            });
            result = result.concat(_strippedResources);
        } while (_wrappedResources.length === PAGE_SIZE);
        return result;
    }
    async *getAllResourcesOfTypeGenerator(resourceType) {
        const PAGE_SIZE = this.cacheConfig.resources[resourceType].pageSize;
        let wrappedResources = await db.resources.orderBy('storedAt')
            .filter(resource => resource.resourceType === resourceType)
            .limit(PAGE_SIZE)
            .toArray();
        wrappedResources = db.unwrapResources(wrappedResources);
        const strippedResources = wrappedResources.map((wrappedResource) => {
            return {
                resource: wrappedResource.resource,
                synced: wrappedResource.synced,
            };
        });
        yield strippedResources;
        if (wrappedResources.length < PAGE_SIZE) {
            return;
        }
        let partialResult = wrappedResources;
        let _wrappedResources = [];
        do {
            const lastEntry = partialResult[partialResult.length - 1];
            _wrappedResources = await db.resources.where('storedAt')
                .aboveOrEqual(lastEntry.storedAt)
                .filter(this.fastForward(lastEntry, 'resourceId', (resource) => resource.resourceType === resourceType))
                .limit(PAGE_SIZE)
                .toArray();
            partialResult = db.unwrapResources(_wrappedResources);
            const _strippedResources = partialResult.map((wrappedResource) => {
                return {
                    resource: wrappedResource.resource,
                    synced: wrappedResource.synced,
                };
            });
            yield _strippedResources;
        } while (_wrappedResources.length === PAGE_SIZE);
    }
    fastForward(lastRow, idProp, otherCriteria) {
        let fastForwardComplete = false;
        return (item) => {
            if (fastForwardComplete)
                return otherCriteria(item);
            if (item[idProp] === lastRow[idProp]) {
                fastForwardComplete = true;
            }
            return false;
        };
    }
    async getResourcesWithIdentifiers(ids, searchField) {
        let wrappedResources = await db.resources.where(searchField).anyOf(ids).toArray();
        wrappedResources = db.unwrapResources(wrappedResources);
        const strippedResources = wrappedResources.map((wrappedResource) => {
            return {
                resource: wrappedResource.resource,
                synced: wrappedResource.synced,
            };
        });
        return strippedResources;
    }
    async bulkGet(ids) {
        let wrappedResources = await db.resources.bulkGet(ids);
        wrappedResources = db.unwrapResources(wrappedResources);
        const strippedResources = wrappedResources.map((wrappedResource) => {
            return {
                resource: wrappedResource.resource,
                synced: wrappedResource.synced,
            };
        });
        return strippedResources;
    }
    async putResource(resource, synced = false) {
        const wrappedResource = await db.wrapResourceForStorage(resource, synced);
        let existingResource = await db.resources.get(wrappedResource.resourceId);
        if (!existingResource) {
            await db.resources.add(wrappedResource);
            return;
        }
        else {
            existingResource = db.unwrapResourceFromStorage(existingResource);
            if (this.cacheConfig.resources[resource.resourceType || 'Resource']?.conflictPolicy === ConflictPolicy.Ignore) {
                return;
            }
            else if (this.cacheConfig.resources[resource.resourceType || 'Resource']?.conflictPolicy === ConflictPolicy.NewestByVersionId) {
                if (!existingResource.resource.meta?.versionId || existingResource.resource.meta?.versionId < (resource.meta?.versionId || -1)) {
                    await db.resources.put(wrappedResource);
                }
                return;
            }
            else if (this.cacheConfig.resources[resource.resourceType || 'Resource']?.conflictPolicy === ConflictPolicy.Overwrite) {
                await db.resources.put(wrappedResource, wrappedResource.resourceId);
                return;
            }
            else if (this.cacheConfig.handleConflictInput) {
                const result = await this.cacheConfig.handleConflictInput(wrappedResource, existingResource);
                if (!result) {
                    return;
                }
            }
        }
    }
    async patchResource(resourceId, resource, synced = true) {
        const wrappedResource = await db.wrapResourceForStorage(resource, synced);
        await db.resources.update(resourceId, wrappedResource);
    }
    async moveResource(oldResourceId, resource, synced = true) {
        await this.putResource(resource, synced);
        if (await db.resources.get(oldResourceId)) {
            await db.resources.delete(oldResourceId);
        }
    }
    async putResources(resources, synced = false) {
        const wrappedResources = resources
            .filter(r => Boolean(r))
            .map(resource => {
            return db.wrapResourceForStorage(resource, synced);
        });
        await db.resources.bulkPut(wrappedResources);
    }
    async isAllResourcesPushed() {
        const resources = await db.resources.where({ syncedIdx: 0 }).toArray();
        return resources.length === 0;
    }
    async getUnsyncedResources() {
        let resources = await db.resources.where({ syncedIdx: 0 }).toArray();
        resources = db.unwrapResources(resources);
        return resources.map((resource) => {
            return resource.resource;
        });
    }
    async getLegacyUnsyncedResources() {
        let resources = await db.resources.filter(res => !res.synced).toArray();
        resources = db.unwrapResources(resources);
        return resources.map((resource) => {
            return resource.resource;
        });
    }
    async markAsSyncedFromResponseBundle(bundledResources, bundle) {
        const movedResources = {};
        if (bundle.entry) {
            for (let i = 0; i < bundledResources.length; i++) {
                let sentResource = bundledResources[i];
                if (sentResource.resourceType === 'Observation') {
                    sentResource = removeDataFromValueAttachment(sentResource);
                }
                const oldResourceId = sentResource.id || '';
                const resourceResponse = bundle.entry[i];
                sentResource.id = resourceResponse.response?.location?.split('/')[1];
                if (sentResource?.id && sentResource.id !== oldResourceId) {
                    await this.moveResource(oldResourceId, sentResource, true);
                    movedResources[oldResourceId] = {
                        newId: sentResource.id,
                        resourceType: sentResource.resourceType || '',
                    };
                }
                else {
                    await this.patchResource(oldResourceId, sentResource, true);
                }
            }
        }
        return movedResources;
    }
    async getTotalResources(resourceType) {
        if (resourceType) {
            return await db.resources.where({ resourceType }).count();
        }
        return await db.resources.count();
    }
    async getPrimaryKeysOfType(resourceType) {
        return (await db.resources.where({ resourceType }).sortBy('effectiveDateTime')).map(res => res.resourceId);
    }
    async getLastResourceTimestamp(resourceType) {
        const syncStates = await db.syncState.where({
            resourceType,
        }).limit(1).reverse().sortBy('lastUpdated');
        return syncStates?.pop()?.lastUpdated;
    }
    async setLastResourceTimestamp(resourceType, lastUpdated) {
        await db.syncState.put({
            resourceType,
            lastUpdated,
        }, resourceType);
    }
}
CacheInterfaceService.ɵfac = function CacheInterfaceService_Factory(t) { return new (t || CacheInterfaceService)(); };
CacheInterfaceService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: CacheInterfaceService, factory: CacheInterfaceService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CacheInterfaceService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return []; }, null); })();

class FhirCacheService {
    constructor(config, fhir, cache) {
        this.config = config;
        this.fhir = fhir;
        this.cache = cache;
        this.resourceUpdateSubject$ = new ReplaySubject(1);
        this.resourceUpdateInfoSubject$ = new ReplaySubject(1);
        this.cache.init(config);
        this.fhir.init(config);
    }
    getConfig() {
        return this.config;
    }
    setConfig(config) {
        this.config = config;
        this.cache.setConfig(config);
        this.fhir.init(config);
    }
    async clearAll() {
        await this.cache.clearAll();
    }
    async getResourceById(id, resourceType) {
        const resource = await this.cache.getResource(id, resourceType);
        return resource;
    }
    async putResource(resource) {
        let isStoredInFhir = false;
        const resourceType = resource.resourceType;
        if (window.navigator.onLine) {
            const fhirStoredResource = await this.fhir.putResource(resource);
            if (fhirStoredResource !== null) {
                isStoredInFhir = true;
                if (resourceType === 'Observation') {
                    resource = removeDataFromValueAttachment(fhirStoredResource);
                }
                else {
                    resource = fhirStoredResource;
                }
            }
        }
        await this.cache.putResource(resource, isStoredInFhir);
        if (resourceType) {
            this.resourceUpdateSubject$.next(resourceType);
            this.resourceUpdateInfoSubject$.next({
                resourceTypes: [resourceType],
                fromIncrementalSync: false,
                updatedIds: resource.id ? [resource.id] : [],
            });
        }
        return isStoredInFhir;
    }
    async putResources(resources) {
        const movedResources = {};
        const isStoredInFhirMap = {};
        const distinctResourceTypes = new Set(resources.map(res => res.resourceType));
        if (window.navigator.onLine) {
            let chunkedRequestData = [];
            const _resources = Array.from(resources);
            while (_resources.length > 0) {
                const chunk = _resources.splice(0, 100);
                chunk.forEach(r => {
                    if (r.id) {
                        isStoredInFhirMap[r.id] = false;
                    }
                });
                chunkedRequestData.push(chunk);
                if (chunkedRequestData.length === 5 || _resources.length === 0) {
                    const responseBundles = await Promise.all(chunkedRequestData.map(_chunk => this.fhir.putResources(_chunk)));
                    for (const responseBundle of responseBundles) {
                        if (responseBundle && responseBundle.entry?.length) {
                            const _movedResources = await this.cache.markAsSyncedFromResponseBundle(chunk, responseBundle);
                            Object.assign(movedResources, _movedResources);
                            chunk.forEach(r => {
                                if (r.id) {
                                    isStoredInFhirMap[movedResources[r.id]?.newId || r.id] = true;
                                }
                            });
                            const _resources = chunk.map(res => {
                                if (res.resourceType === 'Observation') {
                                    return removeDataFromValueAttachment(res);
                                }
                                return res;
                            });
                            await this.cache.putResources(_resources, true);
                        }
                        else {
                            await this.cache.putResources(chunk, false);
                        }
                    }
                    chunkedRequestData = [];
                }
            }
        }
        else {
            await this.cache.putResources(resources, false);
        }
        distinctResourceTypes.forEach(type => {
            if (type) {
                this.resourceUpdateSubject$.next(type);
            }
        });
        if (distinctResourceTypes.size) {
            this.resourceUpdateInfoSubject$.next({
                resourceTypes: Array.from(distinctResourceTypes),
                fromIncrementalSync: false,
                updatedIds: Object.keys(isStoredInFhirMap),
            });
        }
        return { isSyncedMap: isStoredInFhirMap, movedResources };
    }
    async getResourcesByType(resourceType, page = 0) {
        let result = [];
        if (!page) {
            const totalCount = await this.cache.getTotalResources(resourceType);
            const pageSize = this.config?.resources[resourceType]?.pageSize || 5000;
            let _page = 0;
            do {
                _page++;
                result = result.concat(await this.cache.getResources(resourceType, _page));
            } while (_page * pageSize < totalCount);
        }
        else {
            result = await this.cache.getResources(resourceType, page);
        }
        return result;
    }
    getAllResourcesByTypeObservable(resourceType) {
        const pageSize = this.config?.resources[resourceType]?.pageSize || 5000;
        return from(this.cache.getPrimaryKeysOfType(resourceType)).pipe(mergeMap(primaryKeys => {
            const _primaryKeys = primaryKeys;
            const requestObservables = [];
            do {
                const pointer = (primaryKeys.length - pageSize) < 0 ? 0 : (primaryKeys.length - pageSize);
                requestObservables.push(from(this.cache.getResourcesWithIdentifiers(_primaryKeys.splice(pointer, _primaryKeys.length), 'resourceId')));
            } while (_primaryKeys.length);
            return merge(...requestObservables, 10);
        }));
    }
    async *getResourcesByTypeGenerator(resourceType, page = 0) {
        if (!page) {
            const primaryKeys = await this.cache.getPrimaryKeysOfType(resourceType);
            const pageSize = this.config?.resources[resourceType]?.pageSize || 5000;
            let _page = 0;
            do {
                _page++;
                const pointer = (primaryKeys.length - pageSize) < 0 ? 0 : (primaryKeys.length - pageSize);
                yield await this.cache.getResourcesWithIdentifiers(primaryKeys.splice(pointer, primaryKeys.length), 'resourceId');
            } while (primaryKeys.length);
        }
        else {
            yield await this.cache.getResources(resourceType, page);
        }
    }
    async getAllResourcesOfType(resourceType) {
        return await this.cache.getAllResourcesOfType(resourceType);
    }
    async *getAllResourcesOfTypeGenerator(resourceType) {
        for await (const fhirResources of this.cache.getAllResourcesOfTypeGenerator(resourceType)) {
            yield fhirResources;
        }
    }
    async getResourcesWithIdentifiers(ids, searchField = 'resourceId') {
        const result = await this.cache.getResourcesWithIdentifiers(ids, searchField);
        return result;
    }
    async getTotalResourcesByType(resourceType) {
        return await this.cache.getTotalResources(resourceType);
    }
    async clearAllAndAddUnsyncedResources() {
        try {
            const legacyUnsyncedResources = await this.cache.getLegacyUnsyncedResources();
            await this.clearAll();
            await this.cache.putResources(legacyUnsyncedResources);
        }
        catch (e) {
            console.warn(e);
        }
    }
    async attemptSync() {
        const unsyncedResources = await this.cache.getUnsyncedResources();
        const updatedResourceTypes = new Set();
        const updatedIds = new Set();
        if (unsyncedResources.length) {
            let chunkedRequestData = [];
            const _unsyncedResources = Array.from(unsyncedResources);
            while (_unsyncedResources.length > 0) {
                const chunk = _unsyncedResources.splice(0, 100);
                chunkedRequestData.push(chunk);
                if (chunkedRequestData.length === 5 || _unsyncedResources.length === 0) {
                    const responseBundles = await Promise.all(chunkedRequestData.map(_chunk => this.fhir.putResources(_chunk)));
                    for (let i = 0; i < responseBundles.length; i++) {
                        const responseBundle = responseBundles[i];
                        if (responseBundle !== null) {
                            const result = await this.cache.markAsSyncedFromResponseBundle(chunkedRequestData[i], responseBundle);
                            Object.values(result).forEach(res => {
                                updatedResourceTypes.add(res.resourceType);
                            });
                            chunk.forEach(r => {
                                if (r.id) {
                                    updatedIds.add(result[r.id]?.newId || r.id);
                                }
                            });
                        }
                    }
                    chunkedRequestData = [];
                }
            }
        }
        let isIncrementalSync = false;
        try {
            const resourceKeys = Object.keys(this.config.resources);
            for (const resourceKey of resourceKeys) {
                const latestTimestamp = await this.cache.getLastResourceTimestamp(resourceKey);
                if (latestTimestamp) {
                    isIncrementalSync = true;
                }
                // const fhirResources = await this.fhir.getAllResources(resourceKey, latestTimestamp);
                for await (const fhirResources of this.fhir.getAllResources(resourceKey, latestTimestamp, this.config.fhirDetails?.device)) {
                    if (fhirResources) {
                        await this.cache.putResources(fhirResources, true);
                        fhirResources.forEach(e => {
                            if (e?.resourceType) {
                                updatedResourceTypes.add(e.resourceType || '');
                            }
                            if (e?.id) {
                                updatedIds.add(e.id);
                            }
                        });
                        const sortedTimestamps = Array.from(fhirResources)
                            .sort((a, b) => {
                            if (a?.meta?.lastUpdated && b?.meta?.lastUpdated) {
                                return new Date(a.meta.lastUpdated).getTime() - new Date(b.meta.lastUpdated).getTime();
                            }
                            return 0;
                        });
                        const lastTimestampOfStoredResource = sortedTimestamps.pop()?.meta?.lastUpdated;
                        if (lastTimestampOfStoredResource) {
                            const _latestTimestamp = new Date(lastTimestampOfStoredResource).getTime();
                            if (!latestTimestamp || (latestTimestamp && _latestTimestamp > latestTimestamp)) {
                                await this.cache.setLastResourceTimestamp(resourceKey, _latestTimestamp);
                            }
                        }
                    }
                }
            }
        }
        catch (e) {
            console.warn(e);
        }
        if (updatedResourceTypes.size) {
            updatedResourceTypes.forEach(type => {
                this.resourceUpdateSubject$.next(type);
            });
            this.resourceUpdateInfoSubject$.next({
                resourceTypes: Array.from(updatedResourceTypes),
                fromIncrementalSync: true,
                updatedIds: Array.from(updatedIds),
            });
        }
    }
    async getCacheState() {
        return {
            synced: await this.cache.isAllResourcesPushed(),
            totalResources: await this.cache.getTotalResources()
        };
    }
}
FhirCacheService.ɵfac = function FhirCacheService_Factory(t) { return new (t || FhirCacheService)(i0.ɵɵinject('cacheConfig'), i0.ɵɵinject(FhirInterfaceService), i0.ɵɵinject(CacheInterfaceService)); };
FhirCacheService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: FhirCacheService, factory: FhirCacheService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FhirCacheService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: ['cacheConfig']
            }] }, { type: FhirInterfaceService }, { type: CacheInterfaceService }]; }, null); })();

class FhirAuthInterceptor {
    constructor(inj) {
        this.fhirCacheService = inj.get(FhirCacheService);
    }
    intercept(request, next) {
        const fhirConfig = this.fhirCacheService.getConfig();
        if (request.url.startsWith(fhirConfig.fhirDetails.fhirUrl)) {
            const headers = {
                Authorization: `Bearer ${fhirConfig.fhirDetails.fhirJWT}`,
            };
            request = request.clone({
                setHeaders: headers
            });
            request.params.set('patient', fhirConfig.fhirDetails.patientId);
        }
        return next.handle(request);
    }
}
FhirAuthInterceptor.ɵfac = function FhirAuthInterceptor_Factory(t) { return new (t || FhirAuthInterceptor)(i0.ɵɵinject(i0.Injector)); };
FhirAuthInterceptor.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: FhirAuthInterceptor, factory: FhirAuthInterceptor.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FhirAuthInterceptor, [{
        type: Injectable
    }], function () { return [{ type: i0.Injector }]; }, null); })();

class FhirCacheModule {
}
FhirCacheModule.ɵfac = function FhirCacheModule_Factory(t) { return new (t || FhirCacheModule)(); };
FhirCacheModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: FhirCacheModule });
FhirCacheModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [
        FhirCacheService,
        FhirInterfaceService,
        CacheInterfaceService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: FhirAuthInterceptor,
            multi: true
        },
    ], imports: [HttpClientModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FhirCacheModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [
                    HttpClientModule,
                ],
                providers: [
                    FhirCacheService,
                    FhirInterfaceService,
                    CacheInterfaceService,
                    {
                        provide: HTTP_INTERCEPTORS,
                        useClass: FhirAuthInterceptor,
                        multi: true
                    },
                ],
                exports: []
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(FhirCacheModule, { imports: [HttpClientModule] }); })();

function removeDataFromValueAttachment(observation) {
    observation.component?.forEach(comp => {
        if (comp.valueAttachment) {
            comp.valueAttachment.data = 'placeholder';
        }
    });
    return observation;
}

/*
 * Public API Surface of fhir-cache
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ConflictPolicy, EvictionPolicy, FhirAuthInterceptor, FhirCacheModule, FhirCacheService, removeDataFromValueAttachment };
